import type { ApiClient } from './client';
import type { ScanVerdict } from './reports';

/**
 * The consumer reports inbox, as `veri-plus-backend` serves it.
 *
 * `routes/v1/productReport.routes.ts`: `GET /v1/product-reports` for any
 * member, validated by `listOrgReportsQuery` (`cursor`, `limit` 1 to 100,
 * `status`, `category`), answering `{ reports, nextCursor, openCount }`; and
 * `PATCH /v1/product-reports/:reportId/status`, org admins only, body
 * `{ status, statusNote? }`, answering `{ report }`. The report is
 * `productReport.model.ts` through its `toJSON`: the reporter's user id is
 * stripped at the source and photos arrive as `{ id, url }` with `url`
 * `/v1/uploads/:id`. Reports on codes no organization owns reach platform
 * staff only, so this inbox never sees them.
 *
 * The board draws no pager, so the screen loads every page and filters
 * locally, as Products does (D7). D13.
 */

export const reportCategories = [
  'suspected_counterfeit',
  'package_mismatch',
  'packaging_tampered',
  'quality_issue',
  'expired_or_damaged',
  'wrong_product_info',
  'other',
] as const;
export type ReportCategory = (typeof reportCategories)[number];

export type ReportStatus = 'open' | 'reviewing' | 'resolved' | 'dismissed';

/** `serialCode.model.ts`'s `serialCodeStatuses`. */
export type CodeStatus = 'active' | 'flagged' | 'retired';

export interface ReportPhoto {
  id: string;
  url: string;
}

export interface ProductReport {
  id: string;
  /** Pseudonymous, stable per reporter within this organization. */
  reporterRef: string;
  contactConsent: boolean;
  /** Present only when the reporter consented, and only on the list route. */
  reporterEmail?: string | null;
  scanEventId: string;
  productId?: string;
  codeId: string;
  /**
   * The code's status now, not at report time; null when the code is gone.
   * Only the list route carries it, `productReport.service.ts`'s
   * `scanContext`, backend `a55d34e`. Absent from any PATCH answer.
   */
  codeStatus?: CodeStatus | null;
  scanVerdict: ScanVerdict;
  category: ReportCategory;
  note?: string;
  sellerHint?: string;
  claimedProduct?: string;
  photos: ReportPhoto[];
  status: ReportStatus;
  statusNote?: string;
  statusChangedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ReportPage {
  reports: ProductReport[];
  nextCursor: string | null;
  openCount: number;
}

/** The API's own ceiling, `paginationQuery` in `validation/common.ts`. */
const pageLimit = 100;

export interface Inbox {
  reports: ProductReport[];
  /** Counted by the API over the whole organization, not over the pages read. */
  openCount: number;
}

/** Follows `nextCursor` until it runs out. A cursor that repeats ends the walk. */
export async function listAllReports(api: ApiClient, signal?: AbortSignal): Promise<Inbox> {
  const reports: ProductReport[] = [];
  let openCount: number;
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const query: string = cursor === null ? '' : `&cursor=${encodeURIComponent(cursor)}`;
    const page: ReportPage = await api.get<ReportPage>(
      `/v1/product-reports?limit=${pageLimit}${query}`,
      signal ? { signal } : {},
    );
    reports.push(...page.reports);
    openCount = page.openCount;
    cursor = page.nextCursor ?? null;
    if (cursor !== null) {
      if (seen.has(cursor)) break;
      seen.add(cursor);
    }
  } while (cursor !== null);
  return { reports, openCount };
}

/** The shell's badge read, exported so a screen test's fake can answer it and keep its own reads exact. */
export const openCountPath = '/v1/product-reports?limit=1';

/** The sidebar badge: the list route's `openCount`, for the price of one report. */
export async function getOpenCount(api: ApiClient, signal?: AbortSignal): Promise<number> {
  const page = await api.get<ReportPage>(openCountPath, signal ? { signal } : {});
  return page.openCount;
}

export type TargetStatus = Exclude<ReportStatus, 'open'>;

/**
 * Moves a report on. The PATCH answers with the model's `toJSON` alone, which
 * never carries `reporterEmail`, so the caller keeps the one it already had.
 */
export async function setReportStatus(
  api: ApiClient,
  report: ProductReport,
  status: TargetStatus,
): Promise<ProductReport> {
  const body = await api.patch<{ report: ProductReport }>(
    `/v1/product-reports/${encodeURIComponent(report.id)}/status`,
    { status },
  );
  return report.reporterEmail === undefined
    ? body.report
    : { ...body.report, reporterEmail: report.reporterEmail };
}

/**
 * Flags the report's code: `PATCH /v1/codes/:codeId/status` with `flagged`,
 * org admins only, `product.routes.ts`. The API answers `{ code }` and pushes
 * "A product you scanned was flagged" to every consumer who scanned it,
 * `consumerNotify.service.ts`. A code already flagged is saved again without
 * a push. The answer is the code alone, so the report is returned with its
 * `codeStatus` set from it. D38.
 */
export async function flagCode(api: ApiClient, report: ProductReport): Promise<ProductReport> {
  const body = await api.patch<{ code: { status: CodeStatus } }>(
    `/v1/codes/${encodeURIComponent(report.codeId)}/status`,
    { status: 'flagged' },
  );
  return { ...report, codeStatus: body.code.status };
}

/** The drawn-nowhere words for a code that is no longer active, D38. */
export const codeStatusLabels: Record<Exclude<CodeStatus, 'active'>, string> = {
  flagged: 'Flagged',
  retired: 'Retired',
};

/** True when the code can still be flagged: active, or its status unknown. */
export const canFlag = (report: ProductReport) =>
  report.codeStatus !== 'flagged' && report.codeStatus !== 'retired';

export const exportPath = '/v1/product-reports/export';

/**
 * The whole inbox as the API's CSV, `GET /v1/product-reports/export`, org
 * admins only, `productReport.routes.ts`. The route takes `status`,
 * `category`, `from` and `to`; none is sent, D38.
 */
export function exportReports(api: ApiClient, signal?: AbortSignal): Promise<Blob> {
  return api.get<Blob>(exportPath, signal ? { signal, blob: true } : { blob: true });
}

/**
 * The name the API's `Content-Disposition` carries, rebuilt here because the
 * client hands back the blob alone: `sendReportsCsv` in
 * `productReport.controller.ts`, `start` for no `from`, today in UTC for no
 * `to`.
 */
export const exportFileName = (now: Date) =>
  `veriplus-reports-start-to-${now.toISOString().slice(0, 10)}.csv`;

/**
 * A photo's bytes, through the API with the session's token. The API answers
 * with a 302 to a signed URL that expires in minutes, or with the bytes
 * themselves; fetch follows either, so the signed URL never reaches the page
 * and there is nothing to copy. `docs/BACKEND-CONTRACT.md`.
 */
export function getPhoto(api: ApiClient, photo: ReportPhoto, signal?: AbortSignal): Promise<Blob> {
  return api.get<Blob>(photo.url, signal ? { signal, blob: true } : { blob: true });
}

/**
 * What each stored category is called. Six are the consumer form's chips,
 * `ReportForm.dc.html`, paired the way `veriplus-mobile-app`'s
 * `reportCategories.ts` pairs them from upstream's `reportCategoryMeta`. The
 * seventh, `wrong_product_info`, has no chip; this board names it.
 */
export const categoryLabels: Record<ReportCategory, string> = {
  suspected_counterfeit: 'Looks fake',
  package_mismatch: 'Not the right product',
  packaging_tampered: 'Packaging tampered',
  quality_issue: 'Taste, smell or look',
  expired_or_damaged: 'Expired or damaged',
  wrong_product_info: 'Wrong product details',
  other: 'Something else',
};

/** Upstream's `reportCategoryMeta.severeAlone`: the categories serious on their own. D13. */
export const severeCategories: ReadonlySet<ReportCategory> = new Set([
  'suspected_counterfeit',
  'package_mismatch',
  'packaging_tampered',
]);

export type StatusFilter = 'open' | 'reviewing' | 'closed' | 'all';

/** The board's four pills. Closed is the two terminal states together. */
export function inFilter(status: ReportStatus, filter: StatusFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'closed') return status === 'resolved' || status === 'dismissed';
  return status === filter;
}

/**
 * What a report can be moved to, in the order the buttons draw. Upstream's
 * `reportStatusTransitions`: open goes to reviewing or dismissed, reviewing to
 * resolved or dismissed, and the terminal two go nowhere. The board draws the
 * open case; the reviewing case is D13.
 */
export function nextStatuses(status: ReportStatus): TargetStatus[] {
  if (status === 'open') return ['reviewing', 'dismissed'];
  if (status === 'reviewing') return ['resolved', 'dismissed'];
  return [];
}

/** The board's `…8f3a·e91b`: the code's last eight characters, split in two. */
export function codeTail(codeId: string): string {
  const tail = codeId.slice(-8);
  return `…${tail.slice(0, 4)}·${tail.slice(4)}`;
}

export const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function dayWord(at: Date, now: Date): 'today' | 'yesterday' | null {
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((start(now) - start(at)) / 86_400_000);
  return days === 0 ? 'today' : days === 1 ? 'yesterday' : null;
}

export const clock = (at: Date) =>
  `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
export const dayMonth = (at: Date) => `${at.getDate()} ${months[at.getMonth()]}`;

/** A list row's time: `today 08:40`, `yesterday`, `10 Sep`, all drawn. */
export function rowWhen(iso: string, now: Date): string {
  const at = new Date(iso);
  const word = dayWord(at, now);
  if (word === 'today') return `today ${clock(at)}`;
  return word ?? dayMonth(at);
}

/** The detail's time: `Today, 08:40` is drawn, the other two follow it. D13. */
export function detailWhen(iso: string, now: Date): string {
  const at = new Date(iso);
  const word = dayWord(at, now);
  const day = word === 'today' ? 'Today' : word === 'yesterday' ? 'Yesterday' : dayMonth(at);
  return `${day}, ${clock(at)}`;
}
